import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { useAlert } from "@/hooks/useAlert";
import { notificationsService } from "@/api/services/notifications.service";
import { Pagination } from "@/components/common/Pagination";
import { NotificationsSection } from "@/features/admin/home/components";
import { getUserDisplayName } from "@/utils/formatDisplayName";
import { NOTIFICATION_TYPE_LABELS } from "@/types/notification.types";
import styles from "./NotificationsTabContent.module.css";
const PAGE_SIZE = 10;
const NOTIFICATION_TYPES = [
    "urgent_action_required",
    "priority_action",
    "normal_action",
    "confirmation",
    "reminder",
];
export const NotificationsTabContent = () => {
    const { user } = useAuth();
    const { confirm } = useAlert();
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedType, setSelectedType] = useState("all");
    const [selectedStatus, setSelectedStatus] = useState("all");
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const fetchNotifications = useCallback(async () => {
        setLoading(true);
        try {
            const params = {
                roles: ["STAFF"],
                limit: PAGE_SIZE,
                offset: (currentPage - 1) * PAGE_SIZE,
                ...(selectedType !== "all" && {
                    types: [selectedType],
                }),
                ...(selectedStatus === "not_actioned" && { unreadOnly: true }),
                ...(selectedStatus === "actioned" && { unreadOnly: false }),
            };
            const res = await notificationsService.getMyNotifications(params);
            const data = res.data.data ?? [];
            setNotifications(data);
            const count = res.data.totalCount ?? 0;
            setTotalCount(count);
            setTotalPages(Math.max(1, Math.ceil(count / PAGE_SIZE)));
        }
        catch {
            setNotifications([]);
            setTotalCount(0);
            setTotalPages(1);
        }
        finally {
            setLoading(false);
        }
    }, [selectedType, selectedStatus, currentPage]);
    useEffect(() => {
        fetchNotifications();
    }, [fetchNotifications]);
    const handlePageChange = useCallback((page) => {
        setCurrentPage(page);
    }, []);
    const handleTypeChange = useCallback((type) => {
        setSelectedType(type);
        setCurrentPage(1);
    }, []);
    const handleStatusChange = useCallback((status) => {
        setSelectedStatus(status);
        setCurrentPage(1);
    }, []);
    const handleNotificationClick = useCallback(async (notification) => {
        const confirmedAction = await confirm("Are you sure you want to close this notification as it will disappear?", "Confirm");
        if (!confirmedAction)
            return;
        const id = notification.id;
        setNotifications((prev) => prev.filter((n) => n.id !== id));
        try {
            await notificationsService.markAsRead(id, {
                actionedByName: getUserDisplayName(user, "Staff"),
            });
            fetchNotifications();
        }
        catch {
            fetchNotifications();
        }
    }, [confirm, fetchNotifications, user]);
    return (_jsxs("div", { className: styles.wrapper, children: [_jsxs("div", { className: styles.filterBar, children: [_jsxs("div", { className: styles.filterRow, children: [_jsx("span", { className: styles.filterLabel, children: "Filter by type:" }), _jsxs("div", { className: styles.filterChips, children: [_jsx("button", { type: "button", className: `${styles.chip} ${selectedType === "all" ? styles.chipActive : ""}`, onClick: () => handleTypeChange("all"), children: "All" }), NOTIFICATION_TYPES.map((type) => (_jsx("button", { type: "button", className: `${styles.chip} ${selectedType === type ? styles.chipActive : ""}`, onClick: () => handleTypeChange(type), children: NOTIFICATION_TYPE_LABELS[type] }, type)))] })] }), _jsxs("div", { className: styles.filterRow, children: [_jsx("span", { className: styles.filterLabel, children: "Filter by status:" }), _jsxs("div", { className: styles.filterChips, children: [_jsx("button", { type: "button", className: `${styles.chip} ${selectedStatus === "all" ? styles.chipActive : ""}`, onClick: () => handleStatusChange("all"), children: "All" }), _jsx("button", { type: "button", className: `${styles.chip} ${selectedStatus === "actioned" ? styles.chipActive : ""}`, onClick: () => handleStatusChange("actioned"), children: "Actioned" }), _jsx("button", { type: "button", className: `${styles.chip} ${selectedStatus === "not_actioned" ? styles.chipActive : ""}`, onClick: () => handleStatusChange("not_actioned"), children: "Not Actioned" })] })] })] }), _jsx("div", { className: styles.listWrapper, children: loading ? (_jsx("div", { className: styles.loading, children: "Loading notifications\u2026" })) : (_jsxs(_Fragment, { children: [_jsx(NotificationsSection, { notifications: notifications, onNotificationClick: handleNotificationClick }), totalCount > 0 && (_jsx(Pagination, { currentPage: currentPage, totalPages: totalPages, pageSize: PAGE_SIZE, onPageChange: handlePageChange }))] })) })] }));
};
